// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SearchAutoCompleteProductAction from "@eisinc/App/Components/SearchAutoCompleteProductAction/SearchAutoCompleteProductAction";
import { trackClickEvent } from "@eisinc/App/CoveoConnector/Services/CoveoService";
import { ClickEventRequest } from "@eisinc/App/CoveoConnector/Types/schema";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import Button from "@eisinc/MobiusOverrides/Button";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

// <XNG-Changes>
const mapStateToProps = (state: CustomAppState) => ({
    trackCoveo: state.xCoveo?.trackCoveo,
    searchHub: state.xCoveo?.searchHub,
    analyticsObject: (state.xCoveo?.analyticsObject as any) || undefined,
});
// </XNG-Changes>

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface AutocompleteProductsStyles {
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    // XNG-Change
    titleLink?: LinkPresentationProps;
    extendedQtyFieldStyles?: TextFieldPresentationProps;
    extendedAddToCartStyles?: InjectableCss;
    productDetailsWrapper?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    productNumberLabel?: TypographyPresentationProps;
    productNumber?: TypographyPresentationProps;
    productActionsWrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    focusedWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    imageGridItem?: GridItemProps;
    erpNumberText?: TypographyPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

const productActionsWrapperClickHandler = (e: any) => e.stopPropagation();

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    // XNG-Changes>
    trackProductClick = (product: ProductAutocompleteItemModel) => {
        const { trackCoveo, searchHub, analyticsObject } = this.props;

        if (!trackCoveo || !analyticsObject) {
            return;
        }

        const targetId = product.styleParentId || product.id;
        const matchingItem = analyticsObject.items?.find((item: any) => item.id === targetId);

        if (!matchingItem) {
            return;
        }

        const documentOpenEventParameter: ClickEventRequest = {
            actionCause: "documentOpen",
            searchQueryUid: analyticsObject.searchQueryUid || translate("my-uid"),
            originLevel1: searchHub,
            searchHub,
            documentUri: product.uri,
            collectionName: product.brandName,
            sourceName: analyticsObject.sourceName || translate("sourceName"),
            documentPosition: (analyticsObject?.items.findIndex((item: any) => item.id === targetId) || 0) + 1,
            documentTitle: matchingItem.documentTitle || product.title,
            documentUrl: matchingItem.documentUrl || product.url,
            documentAuthor: matchingItem.documentAuthor || "",
            queryPipeline: analyticsObject.queryPipeline as string,
            rankingModifier: matchingItem.rankingModifier || "",
            permanentId: matchingItem.permanentId || "",
            uriHash: matchingItem.uriHash || "",
            customData: {}, // we have to send it even if it's an empty object because BE will throw error if it is null
        };

        trackClickEvent(documentOpenEventParameter);
    };
    // </XNG-Changes>

    render() {
        let { products } = this.props;

        products = products.map(product => {
            return {
                ...product,
                canAddToCart: product.properties.canAddToCart === "true",
                canEnterQuantity: product.properties.canEnterQuantity === "true",
                minimumOrderQty: Number(product.properties.minimumOrderQty),
                qtyOrdered: 1,
            };
        });
        const styles = this.styles;
        return (
            <>
                {products.map(product => (
                    <StyledWrapper
                        {...(this.props.focusedItem === product ? styles.focusedWrapper : styles.wrapper)}
                        key={`${product.id!} ${Math.random()}`}
                        onClick={() => {
                            // XNG-Change
                            this.trackProductClick(product);
                            this.props.goToUrl(product.url);
                        }}
                        className="autocomplete-products-container"
                    >
                        <LazyImage {...styles.image} src={product.image} />
                        <StyledWrapper {...styles.infoWrapper} className="autocomplete-products-info-grid-item">
                            <Link {...styles.titleLink} className="autocomplete-products-link">
                                {product.displayTitle}
                            </Link>

                            <StyledWrapper {...styles.productDetailsWrapper}>
                                <StyledWrapper {...styles.productNumbersWrapper}>
                                    {product.properties.yourPartNumber && (
                                        <StyledWrapper className="product-num">
                                            <Typography {...styles.productNumberLabel}>
                                                {siteMessage("Custom_Your_Part_#", "Your Part #:")}&nbsp;
                                            </Typography>
                                            <Typography {...styles.productNumber}>
                                                {product.properties.yourPartNumber}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                    {product.properties.specNumber && (
                                        <StyledWrapper className="product-num">
                                            <Typography {...styles.productNumberLabel}>
                                                {siteMessage("Custom_Spec_#", "Spec #:")}&nbsp;
                                            </Typography>
                                            <Typography {...styles.productNumber}>
                                                {product.properties.specNumber}
                                            </Typography>
                                        </StyledWrapper>
                                    )}

                                    {!product.properties.specNumber &&
                                        !product.properties.yourPartNumber &&
                                        product.manufacturerItemNumber && (
                                            <StyledWrapper className="product-num">
                                                <Typography {...styles.productNumberLabel}>
                                                    {siteMessage("Custom_MFR_#", "MFR #:")}&nbsp;
                                                </Typography>
                                                <Typography {...styles.productNumber}>
                                                    {product.manufacturerItemNumber}
                                                </Typography>
                                            </StyledWrapper>
                                        )}
                                    {(!product.properties.specNumber || !product.properties.yourPartNumber) && (
                                        <StyledWrapper className="product-num">
                                            <Typography {...styles.productNumberLabel}>
                                                {siteMessage("Custom_Part_#", "EIS Part #:")}&nbsp;
                                            </Typography>
                                            <Typography {...styles.productNumber}>
                                                {product.displayErpNumber}
                                            </Typography>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                {product.properties.isVariantParent !== "true" && (
                                    <SearchAutoCompleteProductAction product={product}>
                                        <StyledWrapper
                                            {...styles.productActionsWrapper}
                                            onClick={productActionsWrapperClickHandler}
                                        >
                                            <ProductQuantityOrdered
                                                extendedStyles={styles.extendedQtyFieldStyles}
                                                labelOverride=""
                                                id={`${product.id}-qty`}
                                            />
                                            <ProductAddToCartButton
                                                extendedStyles={styles.extendedAddToCartStyles}
                                                iconOnly={true}
                                                data-test-selector={`actionsAddToCart${product.id}`}
                                                sizeVariant="small"
                                            />
                                        </StyledWrapper>
                                    </SearchAutoCompleteProductAction>
                                )}
                            </StyledWrapper>

                            {product.properties.isVariantParent === "true" && (
                                <Button className="choose-from-options" sizeVariant="small">
                                    {translate("Select Options")}
                                </Button>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </>
        );
    }
}

// XNG-Change: connect for Coveo click tracking
export default connect(mapStateToProps)(AutocompleteProducts);
